import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { ComponentSync, LL_H_C, LL_V_L } from '@nu-art/thunderstorm-frontend/index';
import { _keys, _values, compare, voidFunction } from '@nu-art/ts-common';
import { Component_ReferencesTable, ModuleFE_Value, ModuleFE_Variable, resolveRangeForPrint } from '@app/hcs-frontend';
import { LikelihoodLabelMap, SeverityLabelMap, } from '@app/dp-shared';
import { ModuleFE_DiseaseValue, ModuleFE_DiseaseView, ModuleFE_MultiVar } from '../../../../_entity.js';
import { resolveValueDisplay } from '@app/hcs-shared';
export class PgDev_DPReport_MultiVars extends ComponentSync {
    //######################### Life Cycle #########################
    componentDidUpdate() {
        const heightMap = this.state.diseaseViews.reduce((hm, view) => {
            const current = this.state.viewConfigRefs[view._id].current;
            if (!current)
                return hm;
            hm[view._id] = current.getBoundingClientRect().height;
            return hm;
        }, {});
        if (this.compareHeights(heightMap, this.props.heightMap)) {
            this.props.reportHeights(heightMap);
        }
        else {
            this.setState({ heightMap: this.props.heightMap });
        }
    }
    deriveStateFromProps(nextProps) {
        const state = {};
        state.heightMap = this.state?.heightMap || {};
        //If new height map received, delete current for re-calculate
        if (!compare(nextProps.heightMap, state.heightMap))
            state.heightMap = {};
        state.diseaseViews = ModuleFE_DiseaseView.cache.all();
        state.viewConfigRefs = this.state?.viewConfigRefs || {};
        state.diseaseViews.forEach(view => {
            if (!state.viewConfigRefs[view._id])
                state.viewConfigRefs[view._id] = React.createRef();
        });
        return state;
    }
    //######################### Logic #########################
    refsAreEmpty = () => {
        return _values(this.state.viewConfigRefs).some(ref => !ref?.current);
    };
    compareHeights = (hm1, hm2) => {
        for (const key of _keys(hm1)) {
            if (!hm2[key] || hm1[key] > hm2[key])
                return true;
        }
        return false;
    };
    getMultivarsForView = (view) => {
        return ModuleFE_MultiVar.cache.filter(mv => mv.diseaseProfileId === this.props.diseaseProfile._id &&
            ModuleFE_DiseaseView.filterMultiVarByEnforcedTag(view, mv));
    };
    getDiseaseValuesForMultiVar = (mv) => {
        return ModuleFE_DiseaseValue.cache.filter(dv => dv.multiVarId === mv._id);
    };
    //######################### Render #########################
    renderTabHeader = () => {
        const profile = this.props.diseaseProfile;
        return _jsx("span", { className: 'profile-title', children: profile.label });
    };
    renderMultiVar = (multiVar, key, pref) => {
        if (pref.mustHaveDiseaseValues && this.getDiseaseValuesForMultiVar(multiVar).length === 0)
            return '';
        return _jsxs(LL_V_L, { className: 'multivar', children: [this.renderMultiVarTable(multiVar, key, pref), this.renderMultiVarRefs(multiVar)] }, key);
    };
    renderMultiVarRefs = (multiVar) => {
        if (multiVar.referenceIds.length === 0)
            return '';
        return _jsxs(LL_V_L, { className: 'multivar-references', children: [_jsx("div", { className: 'field-title', children: "References" }), _jsx(Component_ReferencesTable, { blockEdit: true, referenceIds: multiVar.referenceIds || [], onReferenceUpsertCallback: voidFunction, onReferenceDeletedCallback: voidFunction, allowedRefs: undefined })] });
    };
    renderMultiVarTable = (multiVar, key, pref) => {
        const length = multiVar.variables.length + (pref.ignoreLikelihood ? 0 : 1) + (pref.ignoreSeverity ? 0 : 1);
        const diseaseValues = this.getDiseaseValuesForMultiVar(multiVar);
        return _jsxs(LL_V_L, { className: 'multivar-table', style: { gridTemplateColumns: `repeat(${length - 2},1fr) repeat(2,100px)` }, children: [multiVar.variables.map((item, i) => {
                    if (i === 0 && pref.ignoreFirstVar)
                        return '';
                    const variable = ModuleFE_Variable.cache.unique(item.variableId);
                    return _jsx("div", { className: 'grid-cell header', children: variable.name }, `header-${i}`);
                }), !pref.ignoreLikelihood && _jsx("div", { className: 'grid-cell header', children: "Likelihood" }), !pref.ignoreSeverity && _jsx("div", { className: 'grid-cell header', children: "Severity" }), _jsx("div", { className: 'grid-separator' }), diseaseValues?.map((dv, dvi) => {
                    const arr = [];
                    multiVar.variables.forEach((item, i) => {
                        if (i === 0 && pref.ignoreFirstVar)
                            return;
                        //Find the value id in either the values map or the attributes map
                        const valueId = dv.values[item.variableId];
                        if (!valueId)
                            arr.push(_jsx("div", { className: 'grid-cell' }, `${dv._id}-${item.variableId}-${dvi}`));
                        else {
                            const value = ModuleFE_Value.cache.unique(valueId);
                            arr.push(_jsx("div", { className: 'grid-cell', children: resolveValueDisplay(value) }, `${dv._id}-${item.variableId}-${dvi}`));
                        }
                    });
                    if (!pref.ignoreLikelihood)
                        arr.push(_jsx("div", { className: 'grid-cell', children: `${dv.likelihood ? LikelihoodLabelMap[dv.likelihood] : ''} ${resolveRangeForPrint(dv.likelihoodRange)}` }, `${dvi}-likelihood`));
                    if (!pref.ignoreSeverity)
                        arr.push(_jsx("div", { className: 'grid-cell', children: dv.severityFactor ? SeverityLabelMap[dv.severityFactor] : '' }, `${dvi}-severity`));
                    arr.push(_jsx("div", { className: 'grid-separator' }, `${dvi}-sep`));
                    return arr;
                })] }, key);
    };
    render() {
        if (this.refsAreEmpty()) {
            this.forceUpdate();
        }
        return _jsxs(LL_V_L, { className: 'profile', children: [this.renderTabHeader(), _jsx(LL_V_L, { className: 'profile-data', children: this.state.diseaseViews.map((view) => {
                        const height = this.state.heightMap[view._id];
                        const multiVars = this.getMultivarsForView(view);
                        const ignoreTail = ['treatments', 'differential diagnosis'].includes(view.label.toLowerCase());
                        return _jsxs("div", { className: 'view-container', ref: this.state.viewConfigRefs[view._id], style: { height: height ? `${height}px` : '' }, children: [_jsx("div", { className: 'view-container__header', children: view.label }), _jsx(LL_H_C, { className: 'view-container__multivars', children: multiVars?.map((mv, j) => {
                                        return this.renderMultiVar(mv, j, { ignoreSeverity: ignoreTail, ignoreLikelihood: ignoreTail });
                                    }) })] }, view._id);
                    }) })] });
    }
}
//# sourceMappingURL=PgDev_DPReport_MultiVars.js.map